import React, { useMemo } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList,
  LineChart, Line
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Users, Calendar, Activity, TrendingUp, Sparkles, AlertCircle, Brain, MapPin, RotateCcw, UserCheck } from 'lucide-react';

// Paleta cohesiva (emerald + amber + slate)
const PALETTE = [
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#8b5cf6', // violet-500
  '#06b6d4', // cyan-500
  '#ef4444', // red-500
  '#ec4899', // pink-500
  '#84cc16', // lime-500
  '#3b82f6', // blue-500
  '#f97316', // orange-500
  '#14b8a6', // teal-500
];

const formatPct = (n) => (typeof n === 'number' ? `${n.toFixed(1)}%` : '—');

const StatCard = ({ icon: Icon, label, value, sub, tone = 'emerald', testId }) => (
  <Card className="bg-slate-900/60 border-slate-800" data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">{label}</p>
          <p className="text-3xl font-bold text-white">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
        </div>
        <div className={`p-2.5 rounded-lg bg-${tone}-500/10 text-${tone}-400`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, subtitle, children, testId }) => (
  <Card className="bg-slate-900/60 border-slate-800" data-testid={testId}>
    <CardHeader className="pb-2">
      <CardTitle className="text-base text-white">{title}</CardTitle>
      {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

// Tooltip custom uniforme
const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs shadow-xl">
      {label && <p className="text-slate-300 font-medium mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }}>
          {p.name}: <span className="font-semibold">{p.value}</span>
          {p.payload?.pct != null && <span className="text-slate-400 ml-1">({p.payload.pct}%)</span>}
        </p>
      ))}
    </div>
  );
};

export default function Reto21InsightsView({ insights, mode = 'admin' }) {
  // Hooks SIEMPRE antes de cualquier return condicional
  const heatmap = useMemo(() => {
    const crosses = insights?.crosses?.gatillos_por_sustancia || {};
    const sustancias = Object.keys(crosses);
    const gatillos = new Set();
    sustancias.forEach((s) => (crosses[s] || []).forEach((g) => gatillos.add(g.key)));
    const gatillosList = Array.from(gatillos).slice(0, 8);
    return { sustancias, gatillos: gatillosList, crosses };
  }, [insights]);

  if (!insights || insights.summary?.total === 0) {
    return (
      <div className="text-center py-20">
        <AlertCircle className="w-12 h-12 text-slate-500 mx-auto mb-4" />
        <p className="text-slate-400">Aún no hay datos suficientes del Reto 21.</p>
      </div>
    );
  }

  const s = insights.summary || {};
  const sustancias = insights.sustancias || [];
  const gatillos = insights.gatillos_top || [];
  const fortalezas = insights.fortalezas_top || [];
  const habitos = insights.habitos_top || [];
  const yearsBuckets = insights.demographics?.years_buckets || [];
  const ageBuckets = insights.demographics?.age_buckets || [];
  const inicioBuckets = insights.demographics?.inicio_buckets || [];
  const genero = insights.demographics?.genero || [];
  const regiones = insights.demographics?.regiones || [];
  const intentosPrevios = insights.demographics?.intentos_previos || [];
  const patologiaDualPorGenero = insights.crosses?.patologia_dual_por_genero || [];
  const sustanciaPorGenero = insights.crosses?.sustancia_por_genero || {};
  const patologiaDual = insights.patologia_dual || {};
  const paraQueWords = insights.para_que_keywords || [];
  const tendencia = insights.tendencia_mensual || [];

  const GENERO_LABELS = { hombre: 'Hombre', mujer: 'Mujer', no_decir: 'Prefiere no decir' };
  const INTENTOS_LABELS = { nunca: 'Es mi primer intento', '1': '1 vez antes', '2-5': '2 a 5 veces', '6+': '6 veces o más' };

  return (
    <div className="space-y-6" data-testid="reto21-insights-view">
      {/* KPIs principales */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard testId="kpi-total" icon={Users} tone="emerald" label="Participantes" value={s.total ?? 0} />
        <StatCard testId="kpi-drip-active" icon={Activity} tone="cyan" label="Drip activo" value={s.drip_active ?? 0} sub={`${formatPct(s.opt_in_rate)} opt-in`} />
        <StatCard testId="kpi-pdf-sent" icon={Sparkles} tone="amber" label="PDFs entregados" value={s.pdf_sent ?? 0} />
        <StatCard testId="kpi-avg-age" icon={Calendar} tone="violet" label="Edad promedio" value={s.avg_age ? `${s.avg_age} a.` : '—'} />
        <StatCard testId="kpi-avg-years" icon={TrendingUp} tone="orange" label="Años consumo" value={s.avg_years_consumo ?? '—'} sub="promedio" />
        <StatCard testId="kpi-pat-dual" icon={Brain} tone="red" label="Patología dual" value={formatPct(patologiaDual.pct)} sub={`${patologiaDual.with_mental_health || 0} reportan salud mental`} />
      </div>

      {/* KPI extra: edad de inicio (si hay dato) */}
      {s.avg_edad_inicio != null && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="kpi-row-2">
          <StatCard testId="kpi-edad-inicio" icon={UserCheck} tone="amber" label="Edad de inicio (promedio)" value={`${s.avg_edad_inicio} a.`} sub="cuándo empezó el consumo" />
          {regiones[0] && (
            <StatCard testId="kpi-top-region" icon={MapPin} tone="cyan" label="Región más representada" value={regiones[0].key} sub={`${regiones[0].pct}% del total`} />
          )}
          {intentosPrevios.find(i => i.key !== 'nunca') && (
            <StatCard
              testId="kpi-reintentos"
              icon={RotateCcw}
              tone="violet"
              label="Ya lo intentó antes"
              value={formatPct(intentosPrevios.filter(i => i.key !== 'nunca').reduce((a, b) => a + b.pct, 0))}
              sub="al menos 1 vez"
            />
          )}
        </div>
      )}

      {/* Row 1: Donut sustancias + Años consumo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard testId="chart-sustancias" title="Distribución por sustancia" subtitle="¿Qué consumen quienes inician el Reto?">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={sustancias.map((x) => ({ name: x.key, value: x.count, pct: x.pct }))}
                cx="50%" cy="50%" innerRadius={60} outerRadius={100}
                paddingAngle={2} dataKey="value"
              >
                {sustancias.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                ))}
              </Pie>
              <Tooltip content={<DarkTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12, color: '#cbd5e1' }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard testId="chart-years" title="Años de consumo" subtitle="Tiempo de exposición a la sustancia">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={yearsBuckets} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="bucket" stroke="#94a3b8" tick={{ fontSize: 12 }} />
              <YAxis stroke="#94a3b8" tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip content={<DarkTooltip />} />
              <Bar dataKey="count" fill="#10b981" radius={[6, 6, 0, 0]}>
                <LabelList dataKey="count" position="top" fill="#cbd5e1" fontSize={11} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 2: Top gatillos + Top hábitos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard testId="chart-gatillos" title="Top 10 gatillos" subtitle="Lo que dispara el consumo">
          <ResponsiveContainer width="100%" height={Math.max(280, gatillos.length * 28)}>
            <BarChart layout="vertical" data={gatillos.slice(0, 10)} margin={{ left: 10, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 11 }} />
              <YAxis dataKey="key" type="category" stroke="#94a3b8" tick={{ fontSize: 11 }} width={140} />
              <Tooltip content={<DarkTooltip />} />
              <Bar dataKey="count" fill="#f59e0b" radius={[0, 6, 6, 0]}>
                <LabelList dataKey="pct" position="right" fill="#cbd5e1" fontSize={10} formatter={(v) => `${v}%`} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard testId="chart-habitos" title="Top 10 hábitos elegidos" subtitle="Qué están dispuestos a probar">
          <ResponsiveContainer width="100%" height={Math.max(280, habitos.length * 28)}>
            <BarChart layout="vertical" data={habitos.slice(0, 10)} margin={{ left: 10, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 11 }} />
              <YAxis dataKey="key" type="category" stroke="#94a3b8" tick={{ fontSize: 11 }} width={140} />
              <Tooltip content={<DarkTooltip />} />
              <Bar dataKey="count" fill="#06b6d4" radius={[0, 6, 6, 0]}>
                <LabelList dataKey="pct" position="right" fill="#cbd5e1" fontSize={10} formatter={(v) => `${v}%`} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 3: Top fortalezas + Edad */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard testId="chart-fortalezas" title="Top 10 fortalezas" subtitle="Recursos con los que cuentan">
          <ResponsiveContainer width="100%" height={Math.max(280, fortalezas.length * 28)}>
            <BarChart layout="vertical" data={fortalezas.slice(0, 10)} margin={{ left: 10, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 11 }} />
              <YAxis dataKey="key" type="category" stroke="#94a3b8" tick={{ fontSize: 11 }} width={140} />
              <Tooltip content={<DarkTooltip />} />
              <Bar dataKey="count" fill="#8b5cf6" radius={[0, 6, 6, 0]}>
                <LabelList dataKey="pct" position="right" fill="#cbd5e1" fontSize={10} formatter={(v) => `${v}%`} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard testId="chart-age" title="Edades" subtitle="Distribución demográfica">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={ageBuckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="bucket" stroke="#94a3b8" tick={{ fontSize: 12 }} />
              <YAxis stroke="#94a3b8" tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip content={<DarkTooltip />} />
              <Bar dataKey="count" fill="#ec4899" radius={[6, 6, 0, 0]}>
                <LabelList dataKey="pct" position="top" fill="#cbd5e1" fontSize={10} formatter={(v) => `${v}%`} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 4: Género + Intentos previos (solo si hay datos) */}
      {(genero.length > 0 || intentosPrevios.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {genero.length > 0 && (
            <ChartCard testId="chart-genero" title="Distribución por género" subtitle="¿Quiénes están iniciando el Reto?">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={genero.map((x) => ({ name: GENERO_LABELS[x.key] || x.key, value: x.count, pct: x.pct }))}
                    cx="50%" cy="50%" innerRadius={60} outerRadius={100}
                    paddingAngle={2} dataKey="value"
                  >
                    {genero.map((_, i) => (
                      <Cell key={i} fill={['#06b6d4', '#ec4899', '#64748b'][i] || PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<DarkTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12, color: '#cbd5e1' }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {intentosPrevios.length > 0 && (
            <ChartCard testId="chart-intentos" title="Intentos previos de dejar" subtitle="¿Es la primera vez que lo intenta?">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={intentosPrevios.map(i => ({ ...i, label: INTENTOS_LABELS[i.key] || i.key }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="label" stroke="#94a3b8" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip content={<DarkTooltip />} />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[6, 6, 0, 0]}>
                    <LabelList dataKey="pct" position="top" fill="#cbd5e1" fontSize={10} formatter={(v) => `${v}%`} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
      )}

      {/* Row 5: Edad de inicio + Regiones */}
      {(inicioBuckets.some(b => b.count > 0) || regiones.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {inicioBuckets.some(b => b.count > 0) && (
            <ChartCard testId="chart-edad-inicio" title="Edad de inicio del consumo" subtitle="¿Cuándo empezó todo?">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={inicioBuckets}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="bucket" stroke="#94a3b8" tick={{ fontSize: 12 }} />
                  <YAxis stroke="#94a3b8" tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip content={<DarkTooltip />} />
                  <Bar dataKey="count" fill="#f59e0b" radius={[6, 6, 0, 0]}>
                    <LabelList dataKey="pct" position="top" fill="#cbd5e1" fontSize={10} formatter={(v) => `${v}%`} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {regiones.length > 0 && (
            <ChartCard testId="chart-regiones" title="Top regiones de Chile" subtitle="Desde dónde se inicia el Reto">
              <ResponsiveContainer width="100%" height={Math.max(280, regiones.length * 24)}>
                <BarChart layout="vertical" data={regiones.slice(0, 12)} margin={{ left: 10, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="key" type="category" stroke="#94a3b8" tick={{ fontSize: 11 }} width={140} />
                  <Tooltip content={<DarkTooltip />} />
                  <Bar dataKey="count" fill="#10b981" radius={[0, 6, 6, 0]}>
                    <LabelList dataKey="pct" position="right" fill="#cbd5e1" fontSize={10} formatter={(v) => `${v}%`} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
      )}

      {/* Row 6: Patología dual por género (cruce gold) */}
      {patologiaDualPorGenero.length >= 2 && (
        <ChartCard testId="chart-patdual-genero" title="Patología dual por género" subtitle="% que reporta al menos una condición de salud mental">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={patologiaDualPorGenero.map(x => ({ ...x, label: GENERO_LABELS[x.genero] || x.genero }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="label" stroke="#94a3b8" tick={{ fontSize: 12 }} />
              <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip content={<DarkTooltip />} />
              <Bar dataKey="pct" fill="#ef4444" radius={[6, 6, 0, 0]} name="% con patología dual">
                <LabelList dataKey="pct" position="top" fill="#fca5a5" fontSize={12} formatter={(v) => `${v}%`} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Row 7: Sustancia por género — donuts lado a lado (cruce gold #2) */}
      {(() => {
        const generos = Object.keys(sustanciaPorGenero).filter(g => (sustanciaPorGenero[g] || []).length > 0 && g !== 'no_decir');
        if (generos.length < 2) return null;
        // Paleta consistente por sustancia: misma sustancia = mismo color en ambos donuts
        const allSustancias = Array.from(new Set(generos.flatMap(g => (sustanciaPorGenero[g] || []).map(x => x.key))));
        const colorMap = Object.fromEntries(allSustancias.map((s, i) => [s, PALETTE[i % PALETTE.length]]));
        const gridCols = generos.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';
        return (
          <ChartCard
            testId="chart-sustancia-genero"
            title="Sustancia principal · Hombres vs Mujeres"
            subtitle="¿Consumen cosas distintas? Misma sustancia = mismo color en ambos donuts."
          >
            <div className={`grid grid-cols-1 ${gridCols} gap-4`}>
              {generos.map(g => {
                const data = (sustanciaPorGenero[g] || []).map(x => ({ name: x.key, value: x.count, pct: x.pct }));
                return (
                  <div key={g} className="flex flex-col items-center" data-testid={`donut-sust-${g}`}>
                    <p className="text-sm text-slate-300 font-semibold mb-2">{GENERO_LABELS[g] || g}</p>
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie data={data} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2} dataKey="value">
                          {data.map((entry, i) => (
                            <Cell key={i} fill={colorMap[entry.name] || PALETTE[i % PALETTE.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<DarkTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Mini-leyenda con % */}
                    <div className="flex flex-wrap gap-1.5 mt-2 justify-center">
                      {data.slice(0, 4).map(d => (
                        <span key={d.name} className="text-[10px] text-slate-300 px-2 py-0.5 rounded-full bg-slate-800/60 border border-slate-700">
                          <span className="inline-block w-2 h-2 rounded-full mr-1 align-middle" style={{ background: colorMap[d.name] }} />
                          {d.name} <span className="text-slate-400">({d.pct}%)</span>
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </ChartCard>
        );
      })()}



      {/* Row 4: Heatmap sustancia × gatillos */}
      {heatmap.sustancias.length > 0 && heatmap.gatillos.length > 0 && (
        <ChartCard testId="chart-heatmap" title="Gatillos por sustancia" subtitle="Qué dispara el consumo en cada perfil (% dentro de cada sustancia)">
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs" data-testid="heatmap-table">
              <thead>
                <tr>
                  <th className="text-left p-2 text-slate-400 font-medium sticky left-0 bg-slate-900/60">Sustancia</th>
                  {heatmap.gatillos.map((g) => (
                    <th key={g} className="p-2 text-slate-400 font-medium text-center">{g}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {heatmap.sustancias.map((sus) => {
                  const row = heatmap.crosses[sus] || [];
                  const map = Object.fromEntries(row.map((r) => [r.key, r.pct]));
                  return (
                    <tr key={sus} className="border-t border-slate-800">
                      <td className="p-2 text-white font-medium sticky left-0 bg-slate-900/60">{sus}</td>
                      {heatmap.gatillos.map((g) => {
                        const pct = map[g] || 0;
                        const intensity = Math.min(1, pct / 80);
                        const bg = `rgba(245, 158, 11, ${intensity * 0.85 + 0.05})`;
                        return (
                          <td key={g} className="p-2 text-center text-white" style={{ background: bg }}>
                            {pct ? `${pct}%` : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </ChartCard>
      )}

      {/* Row 5: Tendencia mensual */}
      {tendencia.length > 1 && (
        <ChartCard testId="chart-tendencia" title="Tendencia mensual" subtitle="Cuántas personas inician el Reto cada mes">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={tendencia}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="month" stroke="#94a3b8" tick={{ fontSize: 11 }} />
              <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<DarkTooltip />} />
              <Line type="monotone" dataKey="count" stroke="#10b981" strokeWidth={2.5} dot={{ fill: '#10b981', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Insights derivados */}
      <Card className="bg-gradient-to-br from-emerald-950/40 to-slate-900/60 border-emerald-800/40" data-testid="insights-derivados">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-400" />
            Insights derivados
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {patologiaDual.pct > 0 && (
            <p className="text-sm text-slate-200">
              <span className="text-emerald-400 font-bold">{formatPct(patologiaDual.pct)}</span> reporta al menos una condición de salud mental — patología dual.
            </p>
          )}
          {patologiaDual.by_sustancia?.[0] && (
            <p className="text-sm text-slate-200">
              La sustancia con mayor patología dual es <span className="text-amber-400 font-bold">{patologiaDual.by_sustancia[0].sustancia}</span> ({formatPct(patologiaDual.by_sustancia[0].pct)}).
            </p>
          )}
          {gatillos[0] && (
            <p className="text-sm text-slate-200">
              El gatillo más común es <span className="text-amber-400 font-bold">{gatillos[0].key}</span> ({gatillos[0].pct}%), seguido de {gatillos[1]?.key} ({gatillos[1]?.pct}%).
            </p>
          )}
          {habitos[0] && (
            <p className="text-sm text-slate-200">
              El hábito más elegido para recuperarse es <span className="text-cyan-400 font-bold">{habitos[0].key}</span> ({habitos[0].pct}%).
            </p>
          )}
          {fortalezas[0] && (
            <p className="text-sm text-slate-200">
              La fortaleza más reportada es <span className="text-violet-400 font-bold">{fortalezas[0].key}</span> ({fortalezas[0].pct}%).
            </p>
          )}
          {s.avg_edad_inicio != null && (
            <p className="text-sm text-slate-200">
              La edad promedio en que empezó el consumo fue de <span className="text-amber-400 font-bold">{s.avg_edad_inicio} años</span>.
            </p>
          )}
          {intentosPrevios.length > 0 && intentosPrevios.find(i => i.key !== 'nunca') && (
            <p className="text-sm text-slate-200">
              <span className="text-violet-400 font-bold">
                {formatPct(intentosPrevios.filter(i => i.key !== 'nunca').reduce((a, b) => a + b.pct, 0))}
              </span> ya había intentado dejarlo antes — esta no es su primera vez.
            </p>
          )}
          {patologiaDualPorGenero.length >= 2 && (() => {
            const sorted = [...patologiaDualPorGenero].sort((a, b) => b.pct - a.pct);
            const top = sorted[0], low = sorted[sorted.length - 1];
            if (top.pct - low.pct < 5) return null;
            return (
              <p className="text-sm text-slate-200">
                La patología dual es más frecuente en <span className="text-red-400 font-bold">{GENERO_LABELS[top.genero] || top.genero}</span> ({top.pct}%) que en {GENERO_LABELS[low.genero] || low.genero} ({low.pct}%).
              </p>
            );
          })()}
          {regiones[0] && regiones[0].pct < 60 && regiones.length >= 3 && (
            <p className="text-sm text-slate-200">
              Las 3 regiones con más participantes concentran el <span className="text-emerald-400 font-bold">{(regiones[0].pct + (regiones[1]?.pct || 0) + (regiones[2]?.pct || 0)).toFixed(1)}%</span>: {regiones[0].key}, {regiones[1]?.key} y {regiones[2]?.key}.
            </p>
          )}
          {paraQueWords.length > 0 && mode === 'admin' && (
            <div className="text-sm text-slate-200">
              <p className="mb-2">Palabras más frecuentes en la motivación ("¿para qué?"):</p>
              <div className="flex flex-wrap gap-2">
                {paraQueWords.slice(0, 15).map((w) => (
                  <span key={w.word} className="px-2 py-1 rounded-full bg-slate-800/60 border border-slate-700 text-xs text-slate-300">
                    {w.word} <span className="text-emerald-400 ml-1">({w.count})</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
